import { useAudiencesCRUDActions } from '@mm/state/actions/AudiencesCRUDActions';
import { selectAudiencesPagination } from '@mm/state/selectors/messengerAdSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

export const useFetchAudiences = (options?: { skipBusy?: boolean }) => {
  const { skipBusy } = options || {};
  const audiencesCRUDActions = useAudiencesCRUDActions();
  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const pagination = useSelector(selectAudiencesPagination);

  const [isFetching, setIsFetching] = useState(false);
  const fetchState = useRef<{
    busy: boolean;
    nextPage: number;
    pageSize: number;
  }>({
    busy: false,
    nextPage: !pagination?.isLastPage ? pagination.page + 1 : 0,
    pageSize: pagination.perPage,
  });

  const fetchMore = useCallback(async () => {
    if (!fetchState.current.busy && fetchState.current.nextPage) {
      fetchState.current.busy = true;
      setIsFetching(true);
      const res = await audiencesCRUDActions.retrieveAll(
        activeChatbotId,
        fetchState.current.nextPage,
        fetchState.current.pageSize,
        '',
        true,
        { skipBusy },
      );
      setIsFetching(false);
      fetchState.current.busy = false;
      fetchState.current.nextPage =
        res?.pagination.isLastPage === false ? res.pagination.page + 1 : 0;
      return res;
    }
  }, [audiencesCRUDActions, activeChatbotId, skipBusy]);

  const fetch = useCallback(
    (pageNumber: number, pageSize: number, query = '') => {
      return (async () => {
        if (!fetchState.current.busy) {
          fetchState.current.busy = true;
          setIsFetching(true);
          const res = await audiencesCRUDActions.retrieveAll(
            activeChatbotId,
            pageNumber,
            pageSize,
            query,
          );
          setIsFetching(false);
          fetchState.current = {
            busy: false,
            pageSize,
            nextPage:
              res?.pagination.isLastPage === false
                ? res.pagination.page + 1
                : 0,
          };
          return res;
        }
      })();
    },
    [activeChatbotId, audiencesCRUDActions],
  );

  useEffect(() => {
    fetchMore();
  }, [activeChatbotId, fetchMore]);

  return { fetch, fetchMore, isFetching, pagination };
};
